import {AvalancheServerDataParser} from "@nolimitcity/slot-game/bin/game/reel/avalanchereel/AvalancheServerDataParser";
import {IDynamiteExtraValue, IFITHGameData, IFITHInitData, IFITHServerData} from "./data/IFITHServerData";
import {FITHGameMode} from "../gameMode/FITHGameMode";
import {FITHGameConfig} from "../FITHGameConfig";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";

export class FITHServerDataParser extends AvalancheServerDataParser {
    private _prevReelsAfterCollapse: string[][] = [
        ["X", "X", "X", "X", "X", "X"],
        ["X", "X", "X", "X", "X", "X"],
        ["X", "X", "X", "X", "X", "X"],
        ["X", "X", "X", "X", "X", "X"],
        ["X", "X", "X", "X", "X", "X"],
        ["X", "X", "X", "X", "X", "X"]
    ]; // used for #166

    public parseSingleWin(rawData: IFITHGameData): number {
        return parseFloat(rawData.thisAvalancheWinnings);
    }

    public parseAvalancheTotalWin(rawData: IFITHGameData): number {
        return parseFloat(rawData.totalAvalancheRoundWinnings);
    }

    public parseTotalNumberOfWays(rawData: IFITHServerData): string {
        return rawData.totalNumberOfWays.toString();
    }

    public parseActiveRows(rawData: IFITHInitData): number {
        return rawData.activeRows;
    }

    public parseBonusWinTimesBet(rawData: IFITHInitData): number {
        return rawData.bonusWinTimesBet;
    }

    public parseBaseActiveRowsBefore(rawData: IFITHGameData): number {
        return rawData.baseActiveRowsBefore;
    }

    public parseBaseActiveRowsAfter(rawData: IFITHGameData): number {
        return rawData.baseActiveRowsAfter;
    }

    public parseNearWinPositions(rawData: IFITHGameData): boolean[][] {
        const retVal: boolean[][] = [...super.parseNearWinPositions(rawData)],
            maxScatterAllowed: number = 5,
            originalReelsNumber: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL,
            reels: string[][] = rawData.reels;

        // reset it.
        FITHGameConfig.instance.NEAR_WIN_LAST_SCATTER_REEL_ID = (FITHGameConfig.instance.REELS_NUM + 1);

        // #166 -> aim is to find the reel id on which the 5th scatter hits
        let count: number = 0;
        if (rawData.mode !== FITHGameMode.BONUS && this._prevReelsAfterCollapse.length > 0) {
            for (let i: number = 0; i < originalReelsNumber; i++) {
                for (let j: number = 0; j < this._prevReelsAfterCollapse[i].length; j++) {
                    if (this._prevReelsAfterCollapse[i][j] === ReelSymbolName.BONUS) {
                        count++;
                    }
                }
            }

            if (count < maxScatterAllowed) {
                for (let i: number = 0; i < originalReelsNumber; i++) {
                    for (let j: number = 0; j < reels[i].length; j++) {
                        // using previous reelsAfterCollapse to compare latest reels object
                        if (reels[i][j] === ReelSymbolName.BONUS && this._prevReelsAfterCollapse[i][j] !== ReelSymbolName.BONUS) {
                            count++;
                        }
                        if (count === maxScatterAllowed) {
                            FITHGameConfig.instance.NEAR_WIN_LAST_SCATTER_REEL_ID = i;
                            break;
                        }
                    }
                    if (count === maxScatterAllowed) {
                        break;
                    }
                }
            }

            // if count is less than max scatters then it should reset again.
            if (count < maxScatterAllowed) {
                FITHGameConfig.instance.NEAR_WIN_LAST_SCATTER_REEL_ID = (FITHGameConfig.instance.REELS_NUM + 1);
            }
        }
        // for nextMode NORMAL, need an array of 6*6 with all values as "X" or null(basically don't want bonus sym in this array)
        this._prevReelsAfterCollapse = (rawData.nextMode === FITHGameMode.NORMAL ? (rawData.reelsAfterCollapse.slice(originalReelsNumber, (2 * originalReelsNumber))) : rawData.reelsAfterCollapse);

        // pushing only 1 value extra as nearWin is checked for next reel id and we will have reel id 6 here, but that is not spinning actually, so it should be avoided.
        retVal.push([]);
        return retVal;
    }

    public parseExplodePositions(rawData: IFITHServerData): boolean[][] {
        return rawData.explodePositions.concat();
    }

    public parseReelData(rawData: IFITHInitData | IFITHGameData): string[][] {
        let parsedReels: string[][] = [];

        //Creates 30 fake reels since the server only sends 6
        if (rawData.mode === FITHGameMode.BONUS) {
            for (let i: number = 0; i < rawData.reels.length; i++) {
                for (let j: number = 0; j < rawData.reels.length; j++) {
                    let fakeReel: string[] = [rawData.reels[i][j], "X", "X", "X", "X", "X"];
                    parsedReels.push(fakeReel);
                }
            }
        } else {
            parsedReels = rawData.reels.concat();
            //Adding dummy reels to server response (need to be 36 reels)
            for (let i: number = 0; i < 30; i++) {
                parsedReels.push(["X", "X", "X", "X", "X", "X"]);
            }
        }

        //If restore into Bonus Game intro we need to set Base Game reels correct

        return parsedReels;
    }

    public parseReelsAfterCollapseData(rawData: IFITHInitData | IFITHGameData): string[][] {
        let parsedReels: string[][] = rawData.reelsAfterCollapse.concat();
        //Adding dummy reels to server response (need to be 36 reels)
        for (let i: number = 0; i < 30; i++) {
            parsedReels.push(["X", "X", "X", "X", "X", "X"]);
        }

        return parsedReels;
    }

    public parseAllWinPositions(rawData: IFITHServerData): boolean[][] {
        let allWinPositions: boolean[][] = rawData.allWinPositions.concat();
        // Adding dummy allWinPositions to server response (need to be 36 allWinPositions) and we can put empty array here, as these indexes shall never be called in the game.
        for (let i: number = 0; i < 30; i++) {
            allWinPositions.push([]);
        }

        return allWinPositions;
    }

    public parseBonusNewPositions(rawData: IFITHGameData): boolean[][] {
        return rawData.bonusNewPositions;
    }

    public parseBonusWinTimesBetBefore(rawData: IFITHGameData): number {
        return rawData.bonusWinTimesBetBefore;
    }

    public parseBonusWinTimesBetAfter(rawData: IFITHGameData): number {
        return rawData.bonusWinTimesBetAfter;
    }

    public parseBonusValuesAfterSpin(rawData: IFITHGameData): number[][] {
        return rawData.bonusValuesAfterSpin;
    }

    public parseBonusPositionsBeforeSpin(rawData: IFITHGameData): boolean[][] {
        return rawData.bonusPositionsBeforeSpin;
    }

    public parseWildTransform(rawData: IFITHGameData): boolean {
        return rawData.wildTransform;
    }

    public parseWildTransformPositions(rawData: IFITHGameData): boolean[][] {
        return rawData.wildTransformPositions;
    }

    public parseWildTransformSymbolPositions(rawData: IFITHGameData): boolean[][] {
        return rawData.wildTransformSymbolPositions;
    }

    public parseWaysWonTotalRound(rawData: IFITHGameData): number {
        return rawData.waysWonTotalRound;
    }

    public parseBonusValues(rawData: IFITHInitData): number[][] {
        return rawData.bonusValues;
    }

    public parseOpenPositionsBonus(rawData: IFITHInitData | IFITHGameData): boolean[][] {
        if ("openPositionsBonusAfter" in rawData) {
            return rawData.openPositionsBonusAfter;
        } else {
            return rawData.openPositionsBonusBefore;
        }
    }

    public parseMultiplierNextSpin(rawData: IFITHGameData): number {
        return rawData.multiplierNextSpin;
    }

    public parseBottomCollectorActivated(rawData: IFITHServerData): boolean[] {
        return rawData.bottomCollectorActivated;
    }

    public parseDwarfPosition(rawData: IFITHServerData): number[] {
        return rawData.dwarfPosition;
    }

    public parseActiveRowsOpenedEnteringBonus(rawData: IFITHInitData | IFITHGameData): number {
        let symOverlay: string[][],
            count: number = 0;

        if ("symbolOverlayAfter" in rawData) {
            symOverlay = rawData.symbolOverlayAfter;
        } else {
            symOverlay = rawData.symbolOverlayBefore;
        }

        // can be depicted from 1 column only, and interested in two(3rd, 4th) indexes
        for (let i: number = 3; i < (symOverlay[0].length - 1); i++) {
            if (symOverlay[0][i].indexOf("X") > -1) {
                count++;
            }
        }

        return count;
    }

    public parseDynamiteExtraValues(rawData: IFITHGameData): IDynamiteExtraValue[] {
        return rawData.dynamiteExtraValues;
    }

    public parseBrokeTheBank(rawData: IFITHGameData): boolean {
        return rawData.brokeTheBank;
    }

    // #354
    public parseSecondDwarfPos(rawData: IFITHGameData, originalReels: string[][]): number[] {
        const secondDwarfPos: number[] = [-1, -1],
            dwarfPos: number[] = rawData.dwarfPosition;

        if (dwarfPos[0] > -1) {
            for (let i: number = 0; i < originalReels.length; i++) {
                const newCoinIdx: number = rawData.bonusNewPositions[i].indexOf(true);
                if (originalReels[i][0] === "DW" && newCoinIdx > -1) {
                    // if reels are different OR reel is same but row is different
                    if ((dwarfPos[0] !== i) || (dwarfPos[0] === i && dwarfPos[1] !== newCoinIdx)) {
                        secondDwarfPos[0] = i;
                        secondDwarfPos[1] = newCoinIdx;

                        return secondDwarfPos;
                    }
                }
            }
        }

        return secondDwarfPos;
    }
}